import { filterClasses } from "@swivl-mono/helpers";
import { TaskState, TicketTask } from "@swivl-mono/types";
import { PopoverPosition, Select, SelectItem, SelectOutlet } from "@swivl-mono/ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoIosMore } from "react-icons/io";

import { enforceTaskCharLimit } from "./taskTextUtils";

type TaskItemProps = {
  task: TicketTask;
  isTemplate?: boolean;
  isEditing?: boolean;
  onToggleState: (completed: boolean) => void;
  onStartAction: () => void;
  onEdit: () => void;
  onSaveEdit: (description: string) => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  /** Anonymous access mode: only the checkbox stays live. */
  locked?: boolean;
};

const menuOptions: SelectItem[] = [
  { label: "Edit", value: "edit" },
  { label: "Delete", value: "delete" }
];

export function TaskItem({
  task,
  isTemplate = false,
  isEditing = false,
  onToggleState,
  onStartAction,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  locked
}: TaskItemProps) {
  const isCompleted = task.state === TaskState.COMPLETED;
  const [editText, setEditText] = useState(task.shortDescription);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (isEditing) {
      setEditText(task.shortDescription);
      const el = textareaRef.current;
      if (el) {
        el.focus();
        // Move cursor to end
        el.setSelectionRange(el.value.length, el.value.length);
        autoResize();
      }
    }
  }, [isEditing, task.shortDescription, autoResize]);

  const handleOptionSelected = (selected: string[]): void => {
    const value = selected[0];
    if (value === "edit") {
      onEdit();
    } else if (value === "delete") {
      onDelete();
    }
  };

  const handleSave = (): void => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== task.shortDescription) {
      onSaveEdit(trimmed);
    } else {
      onCancelEdit();
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const enforced = enforceTaskCharLimit(e.target.value);
    setEditText(enforced);
    e.target.value = enforced;
    autoResize();
  };

  const handleEditKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      onCancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className="flex min-h-[42px] items-center gap-3 rounded border border-orange-500 bg-white px-3 py-2 ring-2 ring-orange-50">
        <div className="h-4 w-4 shrink-0 rounded-sm border border-grey-500 bg-white" />

        <textarea
          ref={textareaRef}
          value={editText}
          onChange={handleEditChange}
          onKeyDown={handleEditKeyDown}
          onBlur={handleSave}
          rows={1}
          className="body-sm min-h-0 min-w-0 flex-1 resize-none bg-transparent p-0 break-words text-black outline-none"
        />

        <div className="flex h-6 w-6 shrink-0 items-center justify-center text-grey-900">
          <IoIosMore className="h-4 w-4 -rotate-90" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[42px] items-center gap-3 rounded border border-grey-200 bg-grey-50 px-3 py-2">
      {/* Checkbox */}
      <button
        type="button"
        onClick={() => onToggleState(!isCompleted)}
        className={filterClasses(
          "flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-sm",
          isCompleted ? "bg-orange-500" : "border border-grey-500 bg-white",
          !isCompleted && (isTemplate ? "border-dashed" : "border-solid")
        )}
      >
        {isCompleted && (
          <svg className="h-4 w-4 text-white" viewBox="0 0 16 16" fill="none">
            <path d="M4 8.5l2.5 2.5 5.5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Description – click to edit; locked mode toggles state instead (editing is blocked either way) */}
      <div
        role="button"
        tabIndex={0}
        onClick={locked ? () => onToggleState(!isCompleted) : onEdit}
        onKeyDown={(e) => e.key === "Enter" && (locked ? onToggleState(!isCompleted) : onEdit())}
        className={filterClasses("min-w-0 flex-1", locked ? "cursor-pointer" : "cursor-text")}
      >
        <div className={filterClasses("body-sm break-words whitespace-pre-wrap", isCompleted ? "text-grey-500 line-through" : "text-black")}>
          {task.shortDescription}
        </div>
      </div>

      {/* Menu dropdown — Edit/Delete, both blocked in locked mode */}
      {!locked && (
        <Select
          options={menuOptions}
          onChange={handleOptionSelected}
          styles={{ [SelectOutlet.POPOVER]: { minWidth: 160, width: "auto" } }}
          position={PopoverPosition.RIGHT}
          customTrigger={() => (
            <button className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-grey-900 hover:text-orange-500" type="button">
              <IoIosMore className="h-4 w-4 -rotate-90" />
            </button>
          )}
        />
      )}
    </div>
  );
}
